/**
 * Answers the CMS REST paths the site builds from a published content
 * snapshot (`lib/content-store.ts`), returning what the CMS returns:
 * `{docs, totalDocs}`.
 *
 * The snapshot holds every published doc of a collection at the deepest
 * depth / joins / populate the site uses, with every field. So the order here
 * is: `where` on the full stored doc, `sort`, `limit`, then per doc the
 * select / joins / depth / populate shaping the CMS applies on the way out.
 *
 * Only the query shapes the site builds are supported (see
 * `__tests__/fixtures/content-snapshot/urls.txt`). Anything else throws
 * `UnsupportedQueryError` rather than guessing.
 */

export type SnapshotDoc = Record<string, unknown>

export interface ContentSnapshot {
  version: string
  locale: string
  generatedAt: string
  collections: Record<string, SnapshotDoc[]>
  globals: Record<string, SnapshotDoc>
  /** `en` only: `locale=all` read of the diseases (id, slug map, _status map). */
  diseasesSlugMap?: SnapshotDoc[]
}

export interface SelectResult {
  docs: SnapshotDoc[]
  totalDocs: number
}

export class UnsupportedQueryError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "UnsupportedQueryError"
  }
}

/** Collections the snapshot carries; a path on any other collection is not answerable. */
export const SNAPSHOT_COLLECTIONS = [
  "diseases",
  "biomarkers",
  "products",
  "faq",
  "faq-categories",
  "categories",
  "countries",
  "insurers",
  "insurance-packages",
  "blog-posts",
  "pricing-variables",
  "testimonials",
  "authors",
  "locations",
  "job-listings",
] as const

/** Payload's default `depth` for a REST read without one. */
const DEFAULT_DEPTH = 2
/** Payload's default page size and default `joins[field][limit]`. */
const DEFAULT_LIMIT = 10

/**
 * Populated-relationship field → the collection its docs belong to, for the
 * fields a `populate[<collection>][…]` clause is used on. A populated doc
 * carries no collection marker, so this is how a clause finds its docs.
 */
const POPULATE_FIELD_COLLECTION: Record<string, string> = {
  symptoms: "symptoms",
  relatedBiomarkers: "biomarkers",
  relatedDiseases: "diseases",
  relatedBlogPosts: "blog-posts",
}

type Tree = { [key: string]: Tree | string }

export interface ParsedCmsPath {
  /** Collection slug, or `globals/<slug>`. */
  resource: string
  /** Request locale, `en` when the path has none. */
  locale: string
  /** The `fallback-locale` param verbatim (`"null"` for the literal), if present. */
  fallbackLocale: string | undefined
  query: Tree
}

/** Parse `a[b][0]=v` style keys into a nested tree (numeric levels stay keyed). */
function parseQuery(queryString: string): Tree {
  const root: Tree = {}
  for (const [rawKey, value] of new URLSearchParams(queryString)) {
    const match = rawKey.match(/^([^[\]]+)((?:\[[^[\]]*\])*)$/)
    if (!match) throw new UnsupportedQueryError(`query key ${rawKey}`)
    const segments = [
      match[1],
      ...[...match[2].matchAll(/\[([^[\]]*)\]/g)].map((m) => m[1]),
    ]
    let node = root
    segments.slice(0, -1).forEach((segment) => {
      const next = node[segment]
      if (typeof next === "string") {
        throw new UnsupportedQueryError(`query key ${rawKey} nests a value`)
      }
      node = node[segment] = next ?? {}
    })
    node[segments[segments.length - 1]] = value
  }
  return root
}

export function parseCmsPath(path: string): ParsedCmsPath {
  const [pathname, ...rest] = path.split("?")
  const query = parseQuery(rest.join("?"))
  const locale = query.locale
  const fallback = query["fallback-locale"]
  return {
    resource: pathname.replace(/^\//, ""),
    locale: typeof locale === "string" && locale ? locale : "en",
    fallbackLocale: typeof fallback === "string" ? fallback : undefined,
    query,
  }
}

/**
 * Whether the snapshot can answer this path. A collection outside
 * {@link SNAPSHOT_COLLECTIONS} cannot; neither can `locale=all` on anything
 * but the diseases (the one slug map the snapshot carries).
 */
export function snapshotCovers(parsed: ParsedCmsPath): boolean {
  if (parsed.resource.startsWith("globals/")) {
    return parsed.resource === "globals/section-visibility"
  }
  if (!(SNAPSHOT_COLLECTIONS as readonly string[]).includes(parsed.resource)) {
    return false
  }
  return parsed.query.locale !== "all" || parsed.resource === "diseases"
}

// ── where ───────────────────────────────────────────────────

interface Leaves {
  values: unknown[]
  /** The path crossed an array or a relationship, so it is multi-valued. */
  multi: boolean
}

/** Every leaf a dotted field path reaches, fanning out over arrays. */
function leavesAt(doc: unknown, field: string): Leaves {
  let nodes: unknown[] = [doc]
  let multi = false
  for (const key of field.split(".")) {
    nodes = nodes.flatMap((node) => {
      const value = (node as SnapshotDoc | null | undefined)?.[key]
      if (Array.isArray(value)) {
        multi = true
        return value
      }
      return [value]
    })
  }
  const values = nodes.map((leaf) =>
    leaf !== null && typeof leaf === "object" && "id" in leaf
      ? (leaf as SnapshotDoc).id
      : leaf
  )
  return { values, multi }
}

const isNil = (v: unknown): boolean => v === null || v === undefined

function matchesField(
  doc: SnapshotDoc,
  field: string,
  operators: Tree | string
): boolean {
  if (typeof operators === "string") {
    throw new UnsupportedQueryError(`where[${field}] without an operator`)
  }
  const { values, multi } = leavesAt(doc, field)
  const present = values.filter((v) => !isNil(v))
  return Object.entries(operators).every(([operator, expected]) => {
    if (typeof expected !== "string") {
      throw new UnsupportedQueryError(`where[${field}][${operator}] nests`)
    }
    switch (operator) {
      case "equals":
        return present.some((v) => String(v) === expected)
      case "in": {
        const wanted = expected.split(",")
        return present.some((v) => wanted.includes(String(v)))
      }
      case "not_equals":
        // A multi-valued path (relationship / array) matches when none of its
        // values equal; a scalar must hold a value that differs (SQL `<>` on
        // NULL drops the row, which is why callers pair it with `exists`).
        return multi
          ? !present.some((v) => String(v) === expected)
          : present.length > 0 && String(present[0]) !== expected
      case "exists":
        return present.length > 0 === (expected === "true")
      case "contains":
        return present.some((v) =>
          String(v).toLowerCase().includes(expected.toLowerCase())
        )
      default:
        throw new UnsupportedQueryError(`where operator ${operator}`)
    }
  })
}

function listOf(node: Tree | string, label: string): Tree[] {
  if (typeof node === "string") {
    throw new UnsupportedQueryError(`where[${label}] is not a list`)
  }
  return Object.keys(node)
    .sort((a, b) => Number(a) - Number(b))
    .map((k) => node[k] as Tree)
}

function matchesWhere(doc: SnapshotDoc, where: Tree): boolean {
  return Object.entries(where).every(([key, node]) => {
    if (key === "and") {
      return listOf(node, "and").every((clause) => matchesWhere(doc, clause))
    }
    if (key === "or") {
      return listOf(node, "or").some((clause) => matchesWhere(doc, clause))
    }
    return matchesField(doc, key, node)
  })
}

// ── sort ────────────────────────────────────────────────────

function compareValues(a: unknown, b: unknown): number {
  if (typeof a === "number" && typeof b === "number") return a - b
  if (typeof a === "boolean" && typeof b === "boolean") {
    return Number(a) - Number(b)
  }
  return String(a).localeCompare(String(b), "en")
}

/** Postgres order: NULLs last ascending, first descending. */
function sortDocs(
  docs: SnapshotDoc[],
  sort: string | undefined
): SnapshotDoc[] {
  // Payload sorts by `-createdAt` when a read names no sort.
  const keys = (sort ?? "-createdAt").split(",").filter(Boolean)
  // Docs equal on every named key come newest first (the CMS adds
  // `-createdAt`); docs equal on that too keep their export order.
  if (!keys.some((k) => k.replace(/^-/, "") === "createdAt")) {
    keys.push("-createdAt")
  }
  return [...docs].sort((a, b) => {
    for (const key of keys) {
      const desc = key.startsWith("-")
      const field = desc ? key.slice(1) : key
      const av = a[field]
      const bv = b[field]
      if (isNil(av) && isNil(bv)) continue
      if (isNil(av)) return desc ? -1 : 1
      if (isNil(bv)) return desc ? 1 : -1
      const diff = compareValues(av, bv)
      if (diff !== 0) return desc ? -diff : diff
    }
    return 0
  })
}

// ── select / joins / depth / populate ───────────────────────

/** A populated relationship doc: Payload stamps every doc with these. */
function isPopulatedDoc(value: unknown): value is SnapshotDoc {
  return (
    value !== null &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    "id" in value &&
    "createdAt" in value &&
    "updatedAt" in value
  )
}

/** A join field's value: `{docs, hasNextPage}`. */
function isJoin(value: unknown): value is { docs: unknown[] } & SnapshotDoc {
  return (
    value !== null &&
    typeof value === "object" &&
    Array.isArray((value as SnapshotDoc).docs) &&
    "hasNextPage" in (value as SnapshotDoc)
  )
}

function checkSelectTree(tree: Tree | string, label: string): void {
  if (typeof tree === "string") {
    if (tree !== "true") {
      throw new UnsupportedQueryError(`${label}=${tree}`)
    }
    return
  }
  Object.entries(tree).forEach(([k, v]) => checkSelectTree(v, `${label}[${k}]`))
}

/** Keep the selected keys (plus `id`). Arrays project row by row. */
function project(value: unknown, select: Tree | string): unknown {
  if (select === "true") return value
  if (typeof select === "string") {
    throw new UnsupportedQueryError(`select value ${select}`)
  }
  if (Array.isArray(value)) return value.map((row) => project(row, select))
  if (value === null || typeof value !== "object") return value
  const source = value as SnapshotDoc
  const out: SnapshotDoc = {}
  if ("id" in source) out.id = source.id
  Object.entries(select).forEach(([key, nested]) => {
    if (key in source) out[key] = project(source[key], nested)
  })
  return out
}

interface ShapeContext {
  depth: number
  populate: Tree
  joinLimits: Tree
}

function shapeValue(
  value: unknown,
  level: number,
  field: string | undefined,
  ctx: ShapeContext
): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => shapeValue(item, level, field, ctx))
  }
  if (value === null || typeof value !== "object") return value
  if (isJoin(value)) {
    const limitNode = level === 0 && field ? ctx.joinLimits[field] : undefined
    const limitRaw =
      limitNode && typeof limitNode === "object" ? limitNode.limit : undefined
    const limit = limitRaw === undefined ? DEFAULT_LIMIT : Number(limitRaw)
    const kept = value.docs.slice(0, limit)
    return {
      ...value,
      docs: kept.map((d) => shapeValue(d, level, field, ctx)),
      hasNextPage: value.hasNextPage === true || value.docs.length > limit,
    }
  }
  if (isPopulatedDoc(value)) {
    if (level + 1 > ctx.depth) return value.id
    const collection = field ? POPULATE_FIELD_COLLECTION[field] : undefined
    const fields = collection ? ctx.populate[collection] : undefined
    // The CMS always selects `deletedAt` on a populated or joined doc.
    const projected = fields
      ? {
          ...(project(value, fields) as SnapshotDoc),
          ...("deletedAt" in value ? { deletedAt: value.deletedAt } : {}),
        }
      : value
    return shapeFields(projected, level + 1, ctx)
  }
  return shapeFields(value as SnapshotDoc, level, ctx)
}

function shapeFields(
  doc: SnapshotDoc,
  level: number,
  ctx: ShapeContext
): SnapshotDoc {
  return Object.fromEntries(
    Object.entries(doc).map(([key, v]) => [key, shapeValue(v, level, key, ctx)])
  )
}

// ── entry points ────────────────────────────────────────────

export interface SelectOptions {
  /**
   * The fallback locale's snapshot, for a diseases content read whose
   * `fallback-locale` names a locale (the borrowed-copy locales): a missing
   * description falls back to that snapshot's.
   */
  fallbackSnapshot?: ContentSnapshot
  /**
   * The snapshot of the locale a joined biomarker's missing field falls back
   * to ({@link joinFallbackLocale}). Without it joins do not fall back.
   */
  joinFallbackSnapshot?: ContentSnapshot
}

/**
 * The one locale Payload falls back to for a joined doc's localized field: the
 * request's `fallback-locale`, else the default `en`; none for `null`/`none`,
 * for the request's own locale, or for `locale=all`.
 */
export function joinFallbackLocale(parsed: ParsedCmsPath): string | undefined {
  if (parsed.resource !== "diseases" || parsed.query.locale === "all") {
    return undefined
  }
  const fb = parsed.fallbackLocale ?? "en"
  return fb === "null" || fb === "none" || fb === parsed.locale ? undefined : fb
}

/**
 * What {@link sourceDocs} reads for a path, so a loader can fetch just that:
 * the slug map alone for `locale=all`, else the path's collection. A diseases
 * read with a `fallback-locale` also reads the locale's `diseasesContent` when
 * it has one (`contentRead`) and, for a borrowed-locale fallback, that other
 * locale's `diseasesContent` (or `diseases` when it has none).
 */
export function sourceNeeds(parsed: ParsedCmsPath): {
  slugMap: boolean
  collection: string
  contentRead: boolean
  borrowedFallback: boolean
} {
  const slugMap = parsed.query.locale === "all"
  const contentRead =
    !slugMap &&
    parsed.resource === "diseases" &&
    parsed.fallbackLocale !== undefined
  return {
    slugMap,
    collection: parsed.resource,
    contentRead,
    borrowedFallback: contentRead && parsed.fallbackLocale !== "null",
  }
}

/** Where a diseases read gets its docs from, for the locale it asks. */
function sourceDocs(
  snapshot: ContentSnapshot,
  parsed: ParsedCmsPath,
  options: SelectOptions
): SnapshotDoc[] {
  const { resource, query, fallbackLocale } = parsed
  if (query.locale === "all") {
    if (!snapshot.diseasesSlugMap) {
      throw new UnsupportedQueryError("snapshot has no diseasesSlugMap")
    }
    return snapshot.diseasesSlugMap
  }
  const docs = snapshot.collections[resource]
  if (!docs) throw new UnsupportedQueryError(`collection ${resource}`)
  const content = sourceNeeds(parsed).contentRead
    ? snapshot.collections.diseasesContent
    : undefined
  if (!content) return docs
  // The content docs carry `id` and the locale's own `name` / `description`
  // (null without a translation); `where` and `sort` (`_status`, `name`) are
  // evaluated on the doc they belong to.
  const byId = new Map(docs.map((d) => [String(d.id), d]))
  const merged = content.map((d) => ({ ...byId.get(String(d.id)), ...d }))
  if (fallbackLocale === "null") return merged
  // Content read that falls back to a borrowed locale: only the description.
  const borrowed = options.fallbackSnapshot
  if (!borrowed) {
    throw new UnsupportedQueryError(
      `fallback-locale=${fallbackLocale} needs that locale's snapshot`
    )
  }
  const borrowedDocs =
    borrowed.collections.diseasesContent ?? borrowed.collections.diseases ?? []
  const borrowedById = new Map(borrowedDocs.map((d) => [String(d.id), d]))
  return merged.map((d) => {
    const own = d.description
    if (typeof own === "string" && own.trim()) return d
    // `name` stays the content doc's own (null without a row): it is what sorts.
    return { ...d, description: borrowedById.get(String(d.id))?.description }
  })
}

/**
 * A biomarker joined into a non-`en` locale's disease has, per field, the
 * locale's own `name`, `slug` or `description` (`biomarkersContent`), else the
 * one fallback locale's ({@link joinFallbackLocale}), else null. A biomarker
 * the locale's `biomarkersContent` lacks keeps the snapshot's values.
 */
function withOwnJoinedBiomarkers(
  docs: SnapshotDoc[],
  snapshot: ContentSnapshot,
  parsed: ParsedCmsPath,
  options: SelectOptions
): SnapshotDoc[] {
  const own = snapshot.collections.biomarkersContent
  if (!own || parsed.resource !== "diseases" || parsed.query.locale === "all") {
    return docs
  }
  const byId = new Map(own.map((b) => [String(b.id), b]))
  const fbCollections = options.joinFallbackSnapshot?.collections
  const fbRows = fbCollections
    ? ((joinFallbackLocale(parsed) === "en"
        ? fbCollections.biomarkers
        : fbCollections.biomarkersContent) ?? [])
    : []
  const fbById = new Map(fbRows.map((b) => [String(b.id), b]))
  const pick = (v: unknown, fb: unknown) => v ?? fb ?? null
  return docs.map((doc) => {
    const join = doc.relatedBiomarkers
    if (!isJoin(join)) return doc
    return {
      ...doc,
      relatedBiomarkers: {
        ...join,
        // The exporter projects joined docs (`populate`), so no timestamps.
        docs: join.docs.map((b) => {
          if (b === null || typeof b !== "object" || !("id" in b)) return b
          const row = byId.get(String(b.id))
          if (!row) return b
          const fb = fbById.get(String(b.id))
          return {
            ...b,
            name: pick(row.name, fb?.name),
            slug: pick(row.slug, fb?.slug),
            description: pick(row.description, fb?.description),
          }
        }),
      },
    }
  })
}

export function selectFromSnapshot(
  snapshot: ContentSnapshot,
  path: string,
  options: SelectOptions = {}
): SelectResult {
  const parsed = parseCmsPath(path)
  const { query } = parsed
  const unsupportedParams = Object.keys(query).filter(
    (k) =>
      ![
        "where",
        "sort",
        "select",
        "limit",
        "pagination",
        "depth",
        "locale",
        "fallback-locale",
        "joins",
        "populate",
      ].includes(k)
  )
  if (unsupportedParams.length > 0) {
    throw new UnsupportedQueryError(`query param ${unsupportedParams[0]}`)
  }

  const source = withOwnJoinedBiomarkers(
    sourceDocs(snapshot, parsed, options),
    snapshot,
    parsed,
    options
  )
  const where = typeof query.where === "object" ? query.where : {}
  const matched = sortDocs(
    source.filter((doc) => matchesWhere(doc, where)),
    typeof query.sort === "string" ? query.sort : undefined
  )

  const paginated = query.pagination !== "false"
  const limit =
    typeof query.limit === "string" ? Number(query.limit) : DEFAULT_LIMIT
  const page = paginated ? matched.slice(0, limit) : matched

  const select = typeof query.select === "object" ? query.select : undefined
  if (select) checkSelectTree(select, "select")
  const ctx: ShapeContext = {
    depth:
      typeof query.depth === "string" ? Number(query.depth) : DEFAULT_DEPTH,
    populate: typeof query.populate === "object" ? query.populate : {},
    joinLimits: typeof query.joins === "object" ? query.joins : {},
  }
  const docs = page.map((doc) =>
    shapeFields(select ? (project(doc, select) as SnapshotDoc) : doc, 0, ctx)
  )
  return { docs, totalDocs: matched.length }
}

/** A global's document (`/globals/<slug>`), as the CMS returns it. */
export function selectGlobalFromSnapshot(
  snapshot: ContentSnapshot,
  path: string
): SnapshotDoc {
  const parsed = parseCmsPath(path)
  const slug = parsed.resource.replace(/^globals\//, "")
  const doc = snapshot.globals[slug]
  if (!doc) throw new UnsupportedQueryError(`global ${slug}`)
  return shapeFields(doc, 0, {
    depth:
      typeof parsed.query.depth === "string"
        ? Number(parsed.query.depth)
        : DEFAULT_DEPTH,
    populate: {},
    joinLimits: {},
  })
}
